/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { SelectField, SelectFieldOption } from '@squiz/sds';
import property from 'lodash/property';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import { EntityScvSchema } from '../../../../Services/single-customer-view';
import { SEGMENT_BUILDER_DATA_IDS, SEGMENT_BUILDER_FORM_SELECT_IDS } from '../../SegmentBuilder.constants';
import { AttributeTypeProps, SelectSegmentAttributeProps } from './SelectSegmentAttribute.props';

/**
 * The `attribute` select field.
 * @param {SelectSegmentAttributeProps} props Props passed to `<SelectSegmentAttribute />`.
 * @returns {JSX.Element} The `<SelectSegmentAttribute />` component.
 */
export const SelectSegmentAttribute = (props: SelectSegmentAttributeProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    errors = {},
    events = null,
    onChange = (): void => {
      // Do nothing.
    },
    preselectedAttribute = ``,
    ruleId = ``,
    schema = null,
    type = `default`,
  } = props;

  const attributeRef = useRef<HTMLInputElement>(null);

  // Labels
  const labels = useMemo(() => {
    return {
      attributeLabel: t(CDP_STRINGS.attributeSelectLabel),
      eventFeedLabel: t(CDP_STRINGS.eventFeedLabel),
      placeholderText: t(CDP_STRINGS.attributeSelectPlaceholder),
    };
  }, [t]);

  // Construct a lookup to turn property names into friendly titles.
  const labelsLookup = useMemo((): Record<string, string> => {
    const result: Record<string, string> = {};
    if (events) {
      Object.entries(events).forEach(([subscriptionId, prop]) => {
        result[`$event.${subscriptionId}`] = prop.name;
      });
    }
    if (schema) {
      Object.entries(property(`properties`)(schema) as EntityScvSchema['properties']).forEach(
        ([propertyName, prop]) => {
          result[propertyName] = prop.title;
        },
      );
    }
    return result;
  }, [events, schema]);

  // Attribute Options
  const selectOptions = useMemo((): Array<SelectFieldOption> => {
    if (!schema || !events) {
      return [];
    }
    if (type === `event-property`) {
      return Object.entries(events).map(([subscriptionId, event]) => {
        return {
          key: `$event.${subscriptionId}`,
          label: event.name,
          value: `$event.${subscriptionId}`,
        };
      });
    } else {
      return Object.entries(property(`properties`)(schema) as EntityScvSchema['properties']).map(
        ([propertyName, prop]) => {
          return {
            key: propertyName,
            label: property(`title`)(prop) as string,
            value: propertyName,
          };
        },
      );
    }
  }, [events, schema, type]);

  const defaultSelectionLabel = property(preselectedAttribute as string)(labelsLookup) || ``;

  // Use pre-selected attribute as default if it's provided.
  let defaultSelection: AttributeTypeProps = ``;
  let defaultOption = undefined;

  if (preselectedAttribute) {
    defaultSelection = preselectedAttribute as AttributeTypeProps;
    defaultOption = {
      key: defaultSelection,
      label: defaultSelectionLabel,
      value: defaultSelection,
    } as SelectFieldOption;
  }

  const [selectedAttribute, setSelectedAttribute] = useState<AttributeTypeProps>(defaultSelection);

  const handleAttributeChange = (option: SelectFieldOption): void => {
    setSelectedAttribute(option.value as AttributeTypeProps);
    if (onChange) {
      onChange(attributeRef, option.value);
    }
  };

  return (
    <SelectField
      callback={handleAttributeChange}
      data-id={SEGMENT_BUILDER_DATA_IDS.segmentAttributeSelect}
      data-type={type}
      defaultOption={defaultOption}
      errorMessage={
        property(`${SEGMENT_BUILDER_FORM_SELECT_IDS.attribute}-${ruleId}`)(errors) as string | null | undefined
      }
      fieldLabel={type === `event-property` ? labels.eventFeedLabel : labels.attributeLabel}
      id={`${SEGMENT_BUILDER_FORM_SELECT_IDS.attribute}-${ruleId}`}
      name={SEGMENT_BUILDER_FORM_SELECT_IDS.attribute}
      options={selectOptions}
      placeholder={labels.placeholderText}
      ref={attributeRef}
      required
      value={selectedAttribute}
    />
  );
};
